import { useState } from 'react';
import { Bot, Lock, MessageSquare, Pencil, SmilePlus } from 'lucide-react';
import type { AttachmentView, CommentReactionKind, CommentView } from '@vwt/contracts';
import { AttachButton, AttachmentChip, CommentAttachments, MAX_FILES_PER_BATCH, useUploader } from './attachments.js';
import { Avatar, Badge, Button, cn, EmptyState, Popover, PopoverContent, PopoverTrigger, Tooltip } from '@vwt/ui';
import { newIdempotencyKey, useApiMutation, useApiQuery, POLL_ACTIVE } from '../lib/api.js';
import { toastError } from '../lib/errors.js';
import { currentLocale, useTranslation } from '../lib/i18n.js';
import { useUnsavedChanges } from '../lib/unsaved.js';
import { MarkdownEditor } from './markdown.js';
import { QueryState, TimeAgo } from './common.js';
import { TranslatableMarkdown } from './translation.js';

/** Comment thread for a request or work item; each comment keeps its original language. */
export function CommentsPanel({ parentKind, parentId }: { parentKind: 'REQUEST' | 'WORK_ITEM'; parentId: string }) {
  const { t } = useTranslation();
  const comments = useApiQuery('listComments', { query: { parentKind, parentId } }, { refetchInterval: POLL_ACTIVE });
  const [draft, setDraft] = useState('');
  useUnsavedChanges(draft.trim() !== '');
  const [key, setKey] = useState(newIdempotencyKey);
  const add = useApiMutation('addComment', { invalidate: ['listComments', 'getWorkItem', 'getRequest', 'listAttachments'] });
  const { upload, uploading } = useUploader(parentKind, parentId);
  const [staged, setStaged] = useState<AttachmentView[]>([]);

  const submit = async () => {
    if (!draft.trim()) return;
    try {
      const attachmentIds = staged.filter((f) => f.scanState !== 'REJECTED').map((f) => f.id);
      await add.mutateAsync({
        body: { parentKind, parentId, bodyMarkdown: draft, originalLocale: currentLocale(), ...(attachmentIds.length ? { attachmentIds } : {}) },
        idempotencyKey: key,
      });
      setDraft('');
      setStaged([]);
      setKey(newIdempotencyKey());
    } catch (error) {
      toastError(error);
    }
  };

  return (
    <section aria-label={t('requests.detail.comments')} className="flex flex-col gap-3">
      <QueryState query={comments} empty={comments.data && comments.data.data.length === 0 ? <EmptyState icon={<MessageSquare />} title={t('requests.detail.comments')} description="—" className="py-6" /> : undefined}>
        <ol className="flex flex-col gap-3">
          {(comments.data?.data ?? []).map((c) => (
            <CommentItem key={c.id} comment={c} />
          ))}
        </ol>
      </QueryState>
      <div className="flex flex-col gap-2">
        <MarkdownEditor value={draft} onChange={setDraft} minRows={3} onSubmit={() => void submit()} ariaLabel={t('requests.detail.comments')} />
        {staged.length ? (
          <div className="flex flex-wrap gap-1.5">
            {staged.map((f) => (
              <AttachmentChip key={f.id} file={f} onRemove={() => setStaged((s) => s.filter((x) => x.id !== f.id))} />
            ))}
          </div>
        ) : null}
        <div className="flex items-center justify-between gap-2">
          <AttachButton
            disabled={!!uploading || staged.length >= MAX_FILES_PER_BATCH}
            onFiles={(files) => void upload(files.slice(0, MAX_FILES_PER_BATCH - staged.length)).then((done) => setStaged((s) => [...s, ...done]))}
          />
          <Button variant="primary" size="sm" onClick={() => void submit()} loading={add.isPending} disabled={!draft.trim() || !!uploading}>
            {t('common.submit')}
          </Button>
        </div>
      </div>
    </section>
  );
}

function CommentItem({ comment }: { comment: CommentView }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(comment.bodyMarkdown);
  useUnsavedChanges(editing && body !== comment.bodyMarkdown);
  const edit = useApiMutation('editComment', { invalidate: ['listComments'] });
  if (comment.archivedAt) return null;
  return (
    <li className="flex gap-2.5">
      {comment.authorKind === 'AGENT' ? (
        <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent-soft-fg">
          <Bot className="size-4" />
        </div>
      ) : (
        <Avatar name={comment.author?.displayName ?? '?'} />
      )}
      <div className="min-w-0 flex-1 rounded-[var(--radius)] border border-border bg-surface px-3 py-2">
        <div className="mb-1 flex flex-wrap items-center gap-2 text-xs">
          <span className="font-medium text-fg">{comment.author?.displayName ?? t('common.unknown')}</span>
          {comment.authorKind === 'IMPORT' ? <Badge tone="outline">Jira</Badge> : null}
          {comment.restrictedNote ? (
            <Badge tone="warning">
              <Lock /> {t('status.visibility.RESTRICTED')}
            </Badge>
          ) : null}
          <TimeAgo iso={comment.createdAt} />
          {comment.editedAt ? <span className="text-fg-subtle">· {t('common.edit')}</span> : null}
          {comment.canEdit && !editing ? (
            <button className="ml-auto text-fg-subtle hover:text-fg" aria-label={t('common.edit')} onClick={() => setEditing(true)}>
              <Pencil className="size-3.5" />
            </button>
          ) : null}
        </div>
        {editing ? (
          <div className="flex flex-col gap-2">
            <MarkdownEditor value={body} onChange={setBody} minRows={3} />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                size="sm"
                variant="primary"
                loading={edit.isPending}
                onClick={async () => {
                  try {
                    await edit.mutateAsync({ params: { id: comment.id }, body: { expectedVersion: comment.version, bodyMarkdown: body } });
                    setEditing(false);
                  } catch (error) {
                    toastError(error);
                  }
                }}
              >
                {t('common.save')}
              </Button>
            </div>
          </div>
        ) : (
          <TranslatableMarkdown entityKind="COMMENT" entityId={comment.id} field="bodyMarkdown" source={comment.bodyMarkdown} sourceLocale={comment.originalLocale} />
        )}
        <CommentAttachments files={comment.attachments} />
        <Reactions comment={comment} />
      </div>
    </li>
  );
}

const REACTION_KINDS: CommentReactionKind[] = ['THUMBS_UP', 'THUMBS_DOWN', 'LAUGH', 'HOORAY', 'CONFUSED', 'HEART', 'ROCKET', 'EYES'];
const REACTION_EMOJI: Record<CommentReactionKind, string> = { THUMBS_UP: '👍', THUMBS_DOWN: '👎', LAUGH: '😄', HOORAY: '🎉', CONFUSED: '😕', HEART: '❤️', ROCKET: '🚀', EYES: '👀' };

/** Reaction chips (toggle your own) and a picker with the fixed reaction set. */
function Reactions({ comment }: { comment: CommentView }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const react = useApiMutation('setCommentReaction', { invalidate: ['listComments'] });
  const mine = new Set(comment.reactions.filter((r) => r.reacted).map((r) => r.reaction));
  const toggle = (reaction: CommentReactionKind) => {
    setOpen(false);
    void react.mutateAsync({ params: { id: comment.id }, body: { reaction, active: !mine.has(reaction) } }).catch(toastError);
  };
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1">
      {comment.reactions.map((r) => {
        const label = t('comments.reactedBy', { names: r.people.join(', '), reaction: t(`comments.reactions.${r.reaction}`) });
        return (
          <Tooltip key={r.reaction} content={label}>
            <button
              type="button"
              aria-pressed={r.reacted}
              aria-label={`${label} (${r.count})`}
              onClick={() => toggle(r.reaction)}
              className={cn('inline-flex h-6 items-center gap-1 rounded-full border px-2 text-xs', r.reacted ? 'border-accent bg-accent-soft text-accent-soft-fg' : 'border-border text-fg-muted hover:bg-muted')}
            >
              <span aria-hidden>{REACTION_EMOJI[r.reaction]}</span>
              {r.count}
            </button>
          </Tooltip>
        );
      })}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" aria-label={t('comments.react')} title={t('comments.react')} className="inline-flex h-6 items-center rounded-full border border-dashed border-border px-1.5 text-fg-subtle hover:bg-muted hover:text-fg">
            <SmilePlus className="size-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="flex w-auto gap-0.5 p-1" aria-label={t('comments.react')}>
          {REACTION_KINDS.map((kind) => (
            <button
              key={kind}
              type="button"
              aria-label={t(`comments.reactions.${kind}`)}
              aria-pressed={mine.has(kind)}
              title={t(`comments.reactions.${kind}`)}
              onClick={() => toggle(kind)}
              className={cn('rounded p-1 text-base leading-none hover:bg-muted', mine.has(kind) && 'bg-accent-soft')}
            >
              {REACTION_EMOJI[kind]}
            </button>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  );
}
